import React, { PropsWithChildren } from "react";

import { useWorkOSAuth } from "@worktango/d2c-service/workOSReact";
import { useBooleanFlagValue } from "@worktango/feature-flags-react";

import { Button, Flex, Heading, Icon, Spacer } from "@kazoohr/confetti";
import { coalesce, coalesceFirstTruthy, doNothing } from "@kazoohr/helpers";
import { Link } from "@kazoohr/router";

import {
  GetCurrentUserQuery,
  useGetCurrentUserQuery,
} from "../../../generated/apiHooks";
import { DevAuthTogglePlatformAdmin } from "../devAuth/DevAuthTogglePlatformAdmin";
import { ViewingAsBar } from "../ViewingAsBar";

import styles from "./constellationWrapper.scss";

export function ConstellationWrapper({ children }: PropsWithChildren) {
  const { data } = useGetCurrentUserQuery({
    onError: doNothing, // Handled elsewhere
  });
  const hasOrgOverride = coalesce(
    data?.currentUser?.organizationOverridden,
    false
  );

  return (
    <div style={{ paddingTop: hasOrgOverride ? "48px" : "0" }}>
      <ViewingAsBar />
      <ConstellationHeader />
      <Spacer size="medium" />
      <div className={styles["constellation-wrapper"]}>{children}</div>
      <Spacer size="extra-large" />
    </div>
  );
}

function getName(
  user?: null | {
    email: string;
    firstName?: string | null;
    lastName?: string | null;
  }
) {
  const name = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return coalesceFirstTruthy(name, user?.email);
}

function ConstellationHeader() {
  const { state, user } = useWorkOSAuth();

  return (
    <div className={styles["constellation-header"]}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        className={styles["constellation-header-inner"]}
      >
        <Link style={{ color: "inherit" }} to="/">
          <Flex alignItems="center" gap="16px">
            <Icon icon="ai_general" size={32} variant="filled" />
            <Heading level="2">Constellation</Heading>
          </Flex>
        </Link>
        <Flex gap="24px" justifyContent="flex-end" alignItems="center">
          {state !== "loading" && user ? (
            <AuthenticatedHeaderContent />
          ) : (
            <UnauthenticatedHeaderContent />
          )}
        </Flex>
      </Flex>
      <DevAuthTogglePlatformAdmin />
    </div>
  );
}

function isPlatformAdmin(
  currentUser?: GetCurrentUserQuery["currentUser"] | null
) {
  return coalesce(currentUser?.isPlatformAdmin, false);
}

function isOrgAdmin(currentUser?: GetCurrentUserQuery["currentUser"] | null) {
  return coalesce(
    currentUser?.organizationMemberships.some((org) => org.role === "ADMIN"),
    false
  );
}

function AuthenticatedHeaderContent() {
  const { user, signOut } = useWorkOSAuth();
  const { data } = useGetCurrentUserQuery({
    onError: doNothing, // Handled elsewhere
  });
  const userName = getName(user);

  return (
    <>
      <div>{userName}</div>
      <Spacer size="small" orientation="horizontal" />
      {isPlatformAdmin(data?.currentUser) && (
        <Button
          data-testid="platform-admin-button"
          to="/admin"
          linkElement={Link}
          variant="system"
          icon="admin"
        >
          Platform Admin
        </Button>
      )}
      {isOrgAdmin(data?.currentUser) && (
        <Button
          data-testid="admin-button"
          to="/organization"
          linkElement={Link}
          variant="system"
        >
          Admin
        </Button>
      )}
      <Button onClick={signOut} variant="system">
        Sign Out
      </Button>
    </>
  );
}

function UnauthenticatedHeaderContent() {
  const { signIn, signUp } = useWorkOSAuth();
  const isSignUpAllowed = useBooleanFlagValue("allow-signup", false);

  return (
    <>
      <Button onClick={signIn} variant="system">
        Sign In
      </Button>
      {isSignUpAllowed && (
        <Button onClick={signUp} variant="primary">
          Sign Up
        </Button>
      )}
    </>
  );
}
